import { AuthState } from "../base/AuthState";
import template from "./TokenTemplate.html";

import { _lcs } from "../../i18n";

/**
 * TokenState — the target of the link in the account-validation mail
 * (#/token/:token).
 *
 * The mail carries a one-use token; opening the link is what turns a
 * `not_validated` account into a usable one. The work is a single
 * `PUT /auth/token/:token`, which the backend resolves by the token's own
 * `action` — so this screen stays a thin shell around "did it work" and does
 * not need to know that the action is `validateEmail`.
 *
 * Usually the guest arrives from their inbox signed out, and the screen sends
 * them to the login once the account is live. But an unconfirmed account may
 * use the app, so the link is just as often opened on a phone that is already
 * signed in — then the token is redeemed without a screen of its own, the
 * session is reloaded (so Planer and Chat open up at once) and the guest lands
 * on the home tab. Hence an AuthState for its full-screen rendering, but one
 * that may always be entered.
 *
 * The same route takes the guest's move from another house (menu
 * "Unterkunft", core/AccommodationSwitch): a "switchGuestAccommodation" token,
 * exchanged for a session at THIS house — signed in or not, the guest lands
 * on the home tab signed in, without a screen of their own.
 *
 * The token is consumed on success, so a reloaded page reports "link no longer
 * valid" rather than silently doing nothing. That is honest — the second
 * request genuinely finds nothing — and the message says the account may
 * already be active, which is the likely reason.
 */
export class TokenState extends AuthState
{
    static ROUTE = "/token/:token";
    static ID = "token";

    canEnter()
    {
        return true;
    }

    async enter()
    {
        if ( this.app.user && this.app.user.guest !== true )
        {
            return this.validateSignedIn();
        }

        // "Als Gast fortfahren" keeps nothing worth holding on to: leave it,
        // so the screen and the login after it render on the auth shell.
        if ( this.app.user )
        {
            this.app.user = null;
            this.app.view.switchToAuth();
        }

        this.renderAuth( template, {} );

        this.bind( "to-login", () => this.app.router.redirect( "/login" ) );
        this.bind( "guest", () => this.app.enterAsGuest() );

        await this.validate();
    }

    async validate()
    {
        const token = this.routeParams.getParam( "token" );
        if ( !token )
        {
            return this._fail( _lcs( "Dieser Link ist unvollständig." ) );
        }

        try
        {
            // A reset link ("Passwort vergessen?") is redeemed with the new
            // password on its own screen — everything else right here.
            const { result : lookup } = await this.app.api.get( "/auth/token/" + encodeURIComponent( token ) );
            if ( lookup && lookup.data && lookup.data.action === "switchGuestAccommodation" )
            {
                return this.switchIn( token );
            }
            if ( lookup && lookup.data && lookup.data.action === "resetPassword" )
            {
                // PasswordResetState.ROUTE with the token filled in.
                this.app.router.redirect( "/password-reset/" + encodeURIComponent( token ) );
                return;
            }

            const { result } = await this.app.api.put( "/auth/token/" + encodeURIComponent( token ) );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected token response." );
            }

            this._succeed();
        }
        catch ( e )
        {
            console.error( e );
            this._fail( this._messageForStatus( +e?.status ) );
        }
    }

    /** The link opened while signed in: redeem, reload the session, go home. */
    async validateSignedIn()
    {
        const token = this.routeParams.getParam( "token" );
        try
        {
            const { result : lookup } = await this.app.api.get( "/auth/token/" + encodeURIComponent( token || "" ) );
            if ( lookup && lookup.data && lookup.data.action === "switchGuestAccommodation" )
            {
                return this.switchIn( token );
            }
            if ( lookup && lookup.data && lookup.data.action === "resetPassword" )
            {
                // The reset screen is for the signed-out; signed in, the
                // password is changed in the settings.
                this.app.view.toast( _lcs( "Du bist bereits angemeldet. Dein Passwort änderst du in den Einstellungen." ), "info", 5000 );
                this.app.router.redirect( "/" );
                return;
            }

            const { result } = await this.app.api.put( "/auth/token/" + encodeURIComponent( token ) );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected token response." );
            }

            await this.app.resolveSession();
            this.app.view.toast( _lcs( "Deine E-Mail-Adresse ist bestätigt." ), "success" );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( this._messageForStatus( +e?.status ), "error", 6000 );
        }
        this.app.router.redirect( "/" );
    }

    /**
     * Arriving from another house: redeem the switch token for a session here,
     * store it like a password login would (LoginState) and go home. Whoever
     * was signed in on this house before is replaced — the guest who just
     * picked this house from the menu is the one holding the phone.
     */
    async switchIn( token )
    {
        try
        {
            const { result } = await this.app.api.put( "/auth/token/" + encodeURIComponent( token ) );
            const sessionToken = result && +result.status === 200 ? result.data?.sessionToken : null;
            if ( !sessionToken )
            {
                throw new Error( "Unexpected switch response." );
            }

            // A previous session's thread must not carry over to this one.
            this.app.chat.reset();
            this.app.storage.saveSessionToken( sessionToken );
            await this.app.resolveSession();
        }
        catch ( e )
        {
            console.error( e );
        }

        if ( this.app.user && this.app.user.guest !== true )
        {
            this.app.router.redirect( "/" );
            return;
        }

        this.app.view.toast( _lcs( "Deine Anmeldung konnte nicht übernommen werden. Bitte melde dich an." ), "error", 6000 );
        this.app.router.redirect( "/login" );
    }

    /* -------------------------------- Outcomes ------------------------------- */

    _succeed()
    {
        this._paint( {
            eyebrow : _lcs( "Willkommen" ),
            // Two halves rather than one string with an <em> in it: markup in a
            // dictionary is markup a translator can break.
            title : _lcs( "Konto" ),
            titleEm : _lcs( "bestätigt" ),
            sub : _lcs( "Deine E-Mail-Adresse ist bestätigt." ),
            icon : "circle-check",
            lead : _lcs( "Du kannst dich jetzt anmelden und deine Gästemappe nutzen." )
        } );
    }

    _fail( message )
    {
        this._paint( {
            eyebrow : _lcs( "Link ungültig" ),
            title : _lcs( "Das hat nicht" ),
            titleEm : _lcs( "geklappt" ),
            sub : _lcs( "Wir konnten deinen Link nicht bestätigen." ),
            icon : "triangle-alert",
            lead : message
        } );
    }

    _messageForStatus( status )
    {
        switch ( status )
        {
            // Consumed or never existed — most often a link that was already
            // opened, so the account is probably fine.
            case 404: return _lcs( "Dieser Link wurde bereits verwendet oder ist nicht mehr gültig. " +
                                   "Versuche dich anzumelden — dein Konto ist möglicherweise schon aktiv." );
            case 410: return _lcs( "Dieser Link ist abgelaufen. Fordere nach dem Anmelden einen neuen an — oder registriere dich mit derselben Adresse noch einmal." );
            default:  return _lcs( "Bitte versuche es später erneut." );
        }
    }

    _paint( { eyebrow, title, titleEm, sub, icon, lead } )
    {
        const set = ( id, value ) =>
        {
            const el = document.querySelector( '[data-id="' + id + '"]' );
            if ( el ) el.textContent = value;
        };

        set( "eyebrow", eyebrow );
        set( "sub", sub );
        set( "lead", lead );

        // The heading carries an <em>. Built as nodes rather than an HTML
        // string — the halves are translations, and a translation is text.
        const titleEl = document.querySelector( '[data-id="title"]' );
        if ( titleEl )
        {
            const emphasis = document.createElement( "em" );
            emphasis.textContent = titleEm;
            titleEl.textContent = title + " ";
            titleEl.appendChild( emphasis );
        }

        const iconEl = document.querySelector( '[data-id="icon"] .icon' );
        if ( iconEl ) iconEl.className = "icon icon--" + icon;

        const actions = document.querySelector( '[data-id="actions"]' );
        if ( actions ) actions.hidden = false;
    }

    bind( id, handler )
    {
        const el = document.querySelector( '[data-id="' + id + '"]' );
        if ( el ) this.addEventListener( el, "click", handler );
    }

    async exit() {}
}
